import { supabaseConfig } from '@/shared/config/supabase'
import { AppError } from '@/shared/lib/appError'
import {
  AI_EDGE_JSON_TIMEOUT_MS,
  buildSupabaseEdgeFunctionUrl,
  createAiEdgeAbortSignal,
  isAiFetchTimeout,
  mapAiFetchFailure,
} from '@/shared/api/aiEdgeFetchUtils'
import {
  parseDocumentDraftPayload,
  type DocumentDraftResponseType,
  type ParsedDocumentDraft,
} from '@/shared/api/parseDocumentDraftResponse'
import { requireAccessTokenForEdge } from '@/shared/supabase/getAccessTokenForEdge'

const EDGE_FETCH_ERROR = 'Failed to send a request to the Edge Function'
const MAX_RETRIES = 2
const RETRY_DELAY_MS = 1500

export type DocumentDraftSurface = 'document' | 'template'

export interface GenerateDocumentDraftPayload {
  surface: DocumentDraftSurface
  documentType: string
  documentTypeLabel: string
  title: string
  summary?: string
  contentDraft?: string
  prompt?: string
  /** Sinal externo de cancelamento (ex.: abort no unmount do componente). */
  signal?: AbortSignal
}

export interface GenerateDocumentDraftResult {
  content: string
  /** Fonte da chave usada pela edge ('user' = BYOK, 'system' = plataforma, 'env' = env). */
  keySource?: string
}

const getFunctionsUrl = (): string => buildSupabaseEdgeFunctionUrl('generate-document-draft')

const invokeWithFetch = async (
  body: GenerateDocumentDraftPayload,
): Promise<{ data: unknown; errorMessage: string | null }> => {
  const { signal: externalSignal, ...jsonBody } = body
  const token = await requireAccessTokenForEdge()

  const { signal, cleanup } = createAiEdgeAbortSignal(AI_EDGE_JSON_TIMEOUT_MS, externalSignal)

  try {
    const res = await fetch(getFunctionsUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: supabaseConfig.anonKey,
      },
      body: JSON.stringify(jsonBody),
      signal,
    })

    let text: string
    try {
      text = await res.text()
    } finally {
      cleanup()
    }
    let parsed: unknown = null
    try {
      parsed = text ? JSON.parse(text) : null
    } catch {
      parsed = null
    }

    if (!res.ok) {
      const msg = (parsed as { error?: string })?.error ?? (text?.trim() || res.statusText || `Erro ${res.status}`)
      return { data: null, errorMessage: msg }
    }

    return { data: parsed, errorMessage: null }
  } catch (err) {
    cleanup()
    if (isAiFetchTimeout(err, signal) || signal.aborted) {
      throw mapAiFetchFailure(err, signal)
    }
    const msg = err instanceof Error ? err.message : String(err)
    return { data: null, errorMessage: msg }
  }
}

const invokeWithRetry = async (
  body: GenerateDocumentDraftPayload,
): Promise<{ data: unknown; errorMessage: string | null }> => {
  let lastError: string | null = null
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const { data, errorMessage } = await invokeWithFetch(body)
      if (!errorMessage) return { data, errorMessage: null }
      lastError = errorMessage
      const isFetchError = errorMessage.includes(EDGE_FETCH_ERROR) || errorMessage.includes('fetch')
      if (!isFetchError || attempt >= MAX_RETRIES) break
      await new Promise((r) => setTimeout(r, RETRY_DELAY_MS * (attempt + 1)))
    } catch (err) {
      if (err instanceof AppError && (err.code === 'TIMEOUT' || err.code === 'ABORTED')) {
        throw err
      }
      throw err
    }
  }
  return { data: null, errorMessage: lastError }
}

/** Validação mínima alinhada à edge (título ≥2 ou rascunho ≥10). */
export const canRequestDocumentDraft = (title: string, contentDraft: string): boolean => {
  const t = title.trim().length
  const d = contentDraft.trim().length
  return t >= 2 || d >= 10
}

const normalizeDraftRequest = (
  payload: GenerateDocumentDraftPayload,
): GenerateDocumentDraftPayload => ({
  surface: payload.surface,
  documentType: payload.documentType.trim(),
  documentTypeLabel: payload.documentTypeLabel.trim(),
  title: payload.title.trim(),
  summary: payload.summary?.trim() || undefined,
  contentDraft: payload.contentDraft?.trim() || undefined,
  prompt: payload.prompt?.trim() || undefined,
  signal: payload.signal,
})

const loadDocumentDraftResponse = async (
  payload: GenerateDocumentDraftPayload,
): Promise<unknown> => {
  const body = normalizeDraftRequest(payload)

  if (!canRequestDocumentDraft(body.title, body.contentDraft ?? '')) {
    throw new Error('Informe um título (ao menos 2 caracteres) ou um rascunho de conteúdo (ao menos 10 caracteres).')
  }

  const { data, errorMessage } = await invokeWithRetry(body)

  if (errorMessage) {
    if (errorMessage.includes(EDGE_FETCH_ERROR)) {
      throw new Error(
        `${errorMessage} Verifique se a Edge Function "generate-document-draft" está em deploy.`,
      )
    }
    throw new Error(errorMessage)
  }

  return data
}

const assertDraftPayloadPresent = (data: unknown): unknown => {
  const result = data as { error?: string } | null
  if (!result) {
    throw new Error('Resposta vazia do serviço de IA.')
  }

  if ('error' in result && result.error) {
    throw new Error(result.error)
  }

  return result
}

export const generateDocumentDraft = async (
  payload: GenerateDocumentDraftPayload,
): Promise<GenerateDocumentDraftResult> => {
  const result = assertDraftPayloadPresent(await loadDocumentDraftResponse(payload)) as
    | (GenerateDocumentDraftResult & { key_source?: string })
    | { error?: string }

  if (!('content' in result) || typeof result.content !== 'string') {
    throw new Error('Não foi possível obter o texto gerado.')
  }

  const keySource = result.key_source ?? (result as { keySource?: string }).keySource
  return { content: result.content.trim(), keySource }
}

/**
 * Caminho aditivo: o pedido continua igual ao de `generateDocumentDraft`.
 * A leitura da resposta usa o parser (itens na receita, `contentDraft` nos outros tipos).
 */
export const requestParsedDocumentDraft = async (
  type: DocumentDraftResponseType,
  payload: GenerateDocumentDraftPayload,
): Promise<ParsedDocumentDraft> => {
  const result = assertDraftPayloadPresent(await loadDocumentDraftResponse(payload))
  return parseDocumentDraftPayload(type, result)
}
